/**
 * What happens after Plaid Link's update mode (reconnect) succeeds.
 *
 * Update mode returns no new public token, so /plaid/exchange -- the only
 * place accounts used to be linked -- never runs. An account the owner just
 * ticked in Link's account picker (one opened after the original link) was
 * therefore never imported (issue #22). The page now asks the server to link
 * whatever the Item shares and has no link for, THEN syncs, so the new
 * account's transactions arrive in the same pass.
 */
export interface RefreshResult {
  linked_new: number;
  already_linked: number;
}

export interface AfterReconnect {
  linkedNew: number;
  synced: boolean;
  syncError?: string;
}

type Post = <T>(url: string, body: Record<string, never>) => Promise<T>;

/** Refresh the connection's accounts, then sync it. Order matters. */
export async function refreshAndSync(post: Post, id: string): Promise<AfterReconnect> {
  let linkedNew = 0;
  try {
    const r = await post<RefreshResult>(`/plaid/connections/${id}/refresh-accounts`, {});
    linkedNew = r?.linked_new ?? 0;
  } catch {
    // Sync refreshes accounts itself first, so a failed refresh here is not
    // fatal; the count is simply unknown.
  }
  try {
    await post(`/plaid/connections/${id}/sync`, {});
    return { linkedNew, synced: true };
  } catch (e: unknown) {
    return { linkedNew, synced: false, syncError: (e as Error)?.message };
  }
}

/** "Added 2 new accounts." or "" when nothing was added. */
export function addedAccountsMessage(n: number): string {
  if (n <= 0) return '';
  return `Added ${n} new account${n === 1 ? '' : 's'}.`;
}
